/**
 * CrossTabSync - BroadcastChannel-based cross-tab synchronization service
 * 
 * This service provides immediate synchronization of session state changes
 * between browser tabs using the BroadcastChannel API.
 * 
 * Key features:
 * - Immediate synchronization between tabs
 * - Automatic fallback for browsers without BroadcastChannel support
 * - Event deduplication to prevent infinite loops
 * - Integration with SessionStateManager for database operations
 */

export interface CrossTabEvent {
  type: 'session_created' | 'session_updated' | 'session_deleted' | 'timer_started' | 'timer_paused' | 'timer_resumed' | 'timer_stopped' | 'timer_completed';
  sessionId: string;
  userId: string;
  timestamp: number;
  data?: any;
}

export interface CrossTabSyncOptions {
  channelName?: string;
  enableLogging?: boolean;
  maxRetries?: number;
  retryDelay?: number;
}

class CrossTabSync {
  private static instance: CrossTabSync | null = null;
  private channel: BroadcastChannel | null = null;
  private listeners: Map<string, Set<(event: CrossTabEvent) => void>> = new Map();
  private lastEventId: string | null = null;
  private options: Required<CrossTabSyncOptions>;
  private isSupported: boolean;

  private constructor(options: CrossTabSyncOptions = {}) {
    this.options = {
      channelName: options.channelName || 'deepfocus-sync',
      enableLogging: options.enableLogging || false,
      maxRetries: options.maxRetries || 3,
      retryDelay: options.retryDelay || 1000,
    };

    this.isSupported = typeof BroadcastChannel !== 'undefined';
    
    if (this.isSupported) {
      this.initializeChannel();
    } else {
      this.log('BroadcastChannel not supported, cross-tab sync disabled');
    }
  }

  public static getInstance(options?: CrossTabSyncOptions): CrossTabSync {
    if (!CrossTabSync.instance) {
      CrossTabSync.instance = new CrossTabSync(options);
    }
    return CrossTabSync.instance;
  }

  private initializeChannel(): void {
    try {
      this.channel = new BroadcastChannel(this.options.channelName);
      
      this.channel.onmessage = (event: MessageEvent<CrossTabEvent>) => {
        this.handleMessage(event.data);
      };

      this.log('CrossTabSync initialized successfully');
    } catch (error) {
      this.log('Failed to initialize BroadcastChannel:', error);
      this.isSupported = false;
    }
  }

  private handleMessage(event: CrossTabEvent): void {
    // Prevent processing our own events
    if (event.sessionId === this.lastEventId) {
      return;
    }

    this.log('Received cross-tab event:', event);

    // Notify all listeners for this event type
    const listeners = this.listeners.get(event.type);
    if (listeners) {
      listeners.forEach(listener => {
        try {
          listener(event);
        } catch (error) {
          this.log('Error in cross-tab event listener:', error);
        }
      });
    }
  }

  /**
   * Broadcast an event to all other tabs AND dispatch to same-tab listeners
   */
  public broadcast(event: Omit<CrossTabEvent, 'timestamp'>): void {
    const fullEvent: CrossTabEvent = {
      ...event,
      timestamp: Date.now(),
    };

    // Store event ID to prevent processing our own events from BroadcastChannel
    this.lastEventId = event.sessionId;

    // Broadcast to other tabs via BroadcastChannel
    if (this.isSupported && this.channel) {
      try {
        this.channel.postMessage(fullEvent);
        this.log('Broadcasted event to other tabs:', fullEvent);
      } catch (error) {
        this.log('Failed to broadcast event:', error);
      }
    }

    // Also dispatch to same-tab listeners via window event
    // This ensures the current tab also receives the event
    try {
      window.dispatchEvent(new CustomEvent('deepfocus-cross-tab-event', {
        detail: fullEvent
      }));
      this.log('Dispatched event to same tab:', fullEvent);
    } catch (error) {
      this.log('Failed to dispatch same-tab event:', error);
    }
  }

  /**
   * Subscribe to specific event types
   */
  public subscribe(eventType: CrossTabEvent['type'], listener: (event: CrossTabEvent) => void): () => void {
    if (!this.listeners.has(eventType)) {
      this.listeners.set(eventType, new Set());
    }

    this.listeners.get(eventType)!.add(listener);

    // Also listen for same-tab events via window event
    const windowEventHandler = (windowEvent: Event) => {
      const customEvent = windowEvent as CustomEvent<CrossTabEvent>;
      const event = customEvent.detail;
      if (event.type === eventType) {
        try {
          listener(event);
        } catch (error) {
          this.log('Error in same-tab event listener:', error);
        }
      }
    };
    window.addEventListener('deepfocus-cross-tab-event', windowEventHandler);

    this.log(`Subscribed to ${eventType} events`);

    // Return unsubscribe function
    return () => {
      const listeners = this.listeners.get(eventType);
      if (listeners) {
        listeners.delete(listener);
        if (listeners.size === 0) {
          this.listeners.delete(eventType);
        }
      }
      window.removeEventListener('deepfocus-cross-tab-event', windowEventHandler);
      this.log(`Unsubscribed from ${eventType} events`);
    };
  }

  /**
   * Subscribe to all session-related events
   */
  public subscribeToSessionEvents(listener: (event: CrossTabEvent) => void): () => void {
    const sessionEventTypes: CrossTabEvent['type'][] = [
      'session_created',
      'session_updated',
      'session_deleted',
    ];

    const unsubscribers = sessionEventTypes.map(eventType => 
      this.subscribe(eventType, listener)
    );

    return () => {
      unsubscribers.forEach(unsubscribe => unsubscribe());
    };
  }

  /**
   * Subscribe to all timer-related events
   */
  public subscribeToTimerEvents(listener: (event: CrossTabEvent) => void): () => void {
    const timerEventTypes: CrossTabEvent['type'][] = [
      'timer_started',
      'timer_paused',
      'timer_resumed',
      'timer_stopped',
      'timer_completed',
    ];

    const unsubscribers = timerEventTypes.map(eventType => 
      this.subscribe(eventType, listener)
    );

    return () => {
      unsubscribers.forEach(unsubscribe => unsubscribe());
    };
  }

  /**
   * Check if cross-tab sync is supported
   */
  public isCrossTabSyncSupported(): boolean {
    return this.isSupported;
  }

  /**
   * Get the number of active listeners
   */
  public getListenerCount(): number {
    let total = 0;
    this.listeners.forEach(listeners => {
      total += listeners.size;
    });
    return total;
  }

  /**
   * Clean up resources
   */
  public destroy(): void {
    if (this.channel) {
      this.channel.close();
      this.channel = null;
    }
    this.listeners.clear();
    this.lastEventId = null;
    this.log('CrossTabSync destroyed');
  }

  private log(message: string, ...args: any[]): void {
    if (this.options.enableLogging) {
      // Only log important events, not every subscription/unsubscription
      const importantEvents = [
        'initialized',
        'Failed to',
        'Error',
        'Broadcasted event',
        'Received cross-tab event'
      ];
      
      const shouldLog = importantEvents.some(event => message.includes(event));
      
      if (shouldLog) {
        console.log(`[CrossTabSync] ${message}`, ...args);
      }
    }
  }
}

// Export singleton instance
export const crossTabSync = CrossTabSync.getInstance({
  enableLogging: import.meta.env.DEV,
});

export default crossTabSync;
